import React, { useEffect, useRef, useState } from "react";
import { Lock, Ticket, Heart, Wallet, Loader } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import VideoPlayer from "./VideoPlayer";

/**
 * PpvPreviewLayer — drop-in replacement for the old "ppv-gate" used in
 * Watch.jsx. Plays the full video for `ppv_preview_seconds`, then surfaces
 * a paywall overlay with three ways to unlock:
 *
 *   1. **Pay with wallet** (instant, no Stripe redirect) — visible only
 *      when wallet balance ≥ the price; calls /api/wallet/ppv-unlock/{id}.
 *   2. **Stripe Checkout** — existing /api/videos/{id}/ppv/checkout flow.
 *   3. **Tip the host** — tap a chip ($1/$5/$10/$25), debits wallet, 100%
 *      of split goes to creator (per admin-configured PPV split).
 *
 * The user has explicitly opted into c) "tip menu on paywall + live + regular
 * video player" → this same component handles the regular VOD case; the
 * standalone tip button on Watch (`showTip` state) covers non-PPV titles.
 */
export default function PpvPreviewLayer({ video, startAt, onProgress, onEnded, onUnlocked }) {
  const wrapRef = useRef(null);
  const previewSec = Math.max(0, Number(video?.ppv_preview_seconds || 30));
  const [paywallOpen, setPaywallOpen] = useState(previewSec === 0);
  const [previewLeft, setPreviewLeft] = useState(previewSec);
  const [wallet, setWallet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tipBusy, setTipBusy] = useState(0);
  const [err, setErr] = useState("");
  const [tipMsg, setTipMsg] = useState("");

  // Pause the underlying <video> when paywall opens. VideoPlayer is a
  // separate component but it always renders a single <video> tag inside
  // our wrapper, so a DOM query is safe enough.
  const pauseVideo = () => {
    const v = wrapRef.current?.querySelector("video");
    if (v) {
      try { v.pause(); } catch {}
    }
  };

  // Wrap the consumer's onProgress callback to also drive the preview timer.
  const onProgressWrapped = (currentTime, duration) => {
    onProgress?.(currentTime, duration);
    if (paywallOpen) return;
    const left = Math.max(0, Math.ceil(previewSec - currentTime));
    setPreviewLeft(left);
    if (currentTime >= previewSec) {
      pauseVideo();
      setPaywallOpen(true);
    }
  };

  // Wall-clock fallback so autoplay-blocked or stalled players still surface
  // the paywall after the preview window elapses.
  useEffect(() => {
    if (paywallOpen || previewSec <= 0) return;
    const t = setTimeout(() => {
      pauseVideo();
      setPaywallOpen(true);
    }, previewSec * 1000);
    return () => clearTimeout(t);
  }, [paywallOpen, previewSec]);

  // Pull wallet balance once when paywall opens (so we can show "Pay with wallet" CTA).
  useEffect(() => {
    if (!paywallOpen) return;
    api.get("/funds/me").then((r) => setWallet(r.data || null)).catch(() => setWallet(null));
  }, [paywallOpen]);

  const startStripe = async () => {
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post(`/videos/${video.id}/ppv/checkout`, { origin_url: window.location.origin });
      if (data.already_purchased) { onUnlocked?.(); return; }
      window.location.href = data.url;
    } catch (e) {
      setBusy(false);
      setErr(e?.response?.data?.detail || "Couldn't start checkout. Try again.");
    }
  };

  const payWithWallet = async () => {
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post(`/funds/ppv-unlock/${video.id}`, {});
      if (data.already_purchased || data.ok) {
        onUnlocked?.();
        const v = wrapRef.current?.querySelector("video");
        try { v?.play(); } catch {}
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Wallet payment failed. Add funds and try again.");
    } finally {
      setBusy(false);
    }
  };

  const sendTip = async (amount) => {
    setErr("");
    setTipBusy(amount);
    try {
      const { data } = await api.post(`/funds/tip`, {
        creator_id: video.creator_id,
        amount: Number(amount),
        message: tipMsg.trim() || undefined,
        video_id: video.id,
      });
      // Refresh wallet balance shown on overlay
      setWallet((w) => w ? { ...w, balance_usd: data.balance_usd } : w);
      setTipMsg("");
      // Toast-y feedback inline (no need for sonner here — keeps overlay self-contained)
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Tip failed. Add funds to your wallet first.");
    } finally {
      setTipBusy(0);
    }
  };

  const balance = Number(wallet?.balance_usd || 0);
  const price = Number(video?.ppv_price_usd || 0);
  const canPayWithWallet = balance >= price && price > 0;

  return (
    <div ref={wrapRef} data-testid="ppv-preview-layer" className="relative">
      <VideoPlayer
        videoId={video.id}
        src={video.video_url}
        poster={video.thumbnail_url}
        startAt={startAt}
        onProgress={onProgressWrapped}
        onEnded={onEnded}
        vtt={video.vtt_url || video.captions_vtt_url}
        embedUrl={video.is_external && video.transcode_status !== "ready" ? video.embed_url : undefined}
        sourceType={video.is_external ? video.source_type : undefined}
        watermark={(video.is_kids_approved || video.rating === "G" || video.category === "kids") ? "main" : undefined}
      />

      {/* Free-preview countdown ribbon */}
      {!paywallOpen && (
        <div data-testid="ppv-preview-ribbon" className="absolute top-3 left-1/2 -translate-x-1/2 bg-black/85 backdrop-blur border border-tv-accent/60 text-white rounded-full px-4 py-1.5 inline-flex items-center gap-2 z-30 shadow-2xl pointer-events-none">
          <Ticket className="w-3.5 h-3.5 text-tv-accent" />
          <span className="text-[11px] font-display font-bold uppercase tracking-wider">Free preview</span>
          <span className="text-sm font-black tabular-nums">{previewLeft}s</span>
          <span className="text-white/55 text-[10px]">·</span>
          <span className="text-[11px] text-white/85">${price.toFixed(2)} to unlock</span>
        </div>
      )}

      {/* Paywall overlay — opens at preview_seconds */}
      {paywallOpen && (
        <div data-testid="ppv-paywall" className="absolute inset-0 z-40 rounded-2xl overflow-hidden flex flex-col items-center justify-center bg-black/85 backdrop-blur-md text-center p-6">
          {video.thumbnail_url && (
            <img src={video.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/85 to-black" />

          <div className="relative z-10 max-w-lg w-full">
            <div className="text-[11px] uppercase tracking-[0.3em] text-tv-accent font-bold inline-flex items-center gap-2 justify-center">
              <Ticket className="w-4 h-4" /> Pay-Per-View
            </div>
            <h2 className="font-display font-black text-3xl md:text-4xl leading-tight mt-2 text-white">
              Preview's over — unlock the full show
            </h2>
            <p className="text-white/75 mt-3 text-sm max-w-md mx-auto leading-snug">
              You watched the free {previewSec}s sample of <strong>{video.title}</strong>. Unlock unlimited HD replay; revenue is split with the creator.
            </p>

            {err && <div className="text-rose-300 text-sm mt-3" data-testid="ppv-paywall-error">{err}</div>}

            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              {canPayWithWallet && (
                <button
                  data-testid="ppv-pay-wallet-btn"
                  disabled={busy}
                  onClick={payWithWallet}
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-display font-black px-5 py-3 rounded-full disabled:opacity-60"
                >
                  <Wallet className="w-4 h-4" /> Pay ${price.toFixed(2)} with wallet
                </button>
              )}
              <button
                data-testid="ppv-buy-btn"
                disabled={busy}
                onClick={startStripe}
                className="inline-flex items-center gap-2 bg-tv-accent hover:bg-yellow-300 text-black font-display font-black px-5 py-3 rounded-full disabled:opacity-60"
              >
                {busy ? <Loader className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                {busy ? "Redirecting…" : `Unlock for $${price.toFixed(2)}`}
              </button>
              <button
                data-testid="ppv-replay-preview"
                onClick={() => {
                  const v = wrapRef.current?.querySelector("video");
                  if (v) { try { v.currentTime = 0; v.play(); } catch {} }
                  setPaywallOpen(false);
                  setPreviewLeft(previewSec);
                }}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-3 rounded-full"
              >
                Replay preview
              </button>
            </div>

            {/* Tip the host — micro-payments from wallet */}
            <div className="mt-5 border-t border-white/10 pt-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-bold inline-flex items-center gap-1.5">
                <Heart className="w-3 h-3 text-rose-400" /> Tip the host
              </div>
              <div className="text-white/65 text-xs mt-1">
                Wallet: <strong className="text-white">${balance.toFixed(2)}</strong>
                {!canPayWithWallet && balance < 1 && (
                  <> · <Link to="/funds" className="underline text-tv-accent">Add funds</Link></>
                )}
              </div>
              <input
                data-testid="ppv-tip-msg"
                type="text"
                maxLength={140}
                value={tipMsg}
                onChange={(e) => setTipMsg(e.target.value)}
                placeholder="Add a message (optional)"
                className="mt-2 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent placeholder:text-white/35"
              />
              <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                {[1, 5, 10, 25].map((amt) => (
                  <button
                    key={amt}
                    data-testid={`ppv-tip-btn-${amt}`}
                    disabled={tipBusy === amt || balance < amt}
                    onClick={() => sendTip(amt)}
                    className="inline-flex items-center gap-1 bg-rose-500/15 hover:bg-rose-500/30 disabled:opacity-40 text-rose-200 hover:text-white font-bold text-sm px-3 py-1.5 rounded-full border border-rose-500/30"
                  >
                    <Heart className="w-3 h-3" /> ${amt}
                  </button>
                ))}
              </div>
            </div>

            <div className="text-white/45 text-xs mt-4">
              Already bought it? <button
                onClick={async () => {
                  try {
                    const r = await api.get(`/videos/${video.id}/ppv/access`);
                    if (r.data?.has_access) onUnlocked?.();
                    else setErr("No purchase found yet — finish checkout, then tap Refresh.");
                  } catch { setErr("Couldn't refresh access. Check your connection."); }
                }}
                data-testid="ppv-refresh-access"
                className="underline hover:text-white"
              >Refresh access</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
